/**
 * React Portal Component
 *
 * @module    portal
 * @author    Matthew Hamlin  <mhamlin@wayfair.com>
 * @author    Nick Dreckshage <ndreckshage@wayfair.com>
 * @copyright 2018 Wayfair LLC - All rights reserved
 */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {createPortal} from 'react-dom';
import PortalContext, {
  defaultCreateContainer,
  defaultRemoveContainer,
} from '../PortalContext/PortalContext';

class PortalImpl extends Component {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    children: PropTypes.node.isRequired,
    inTestEnvironment: PropTypes.bool,
    createContainer: PropTypes.func,
    removeContainer: PropTypes.func,
  };
  static defaultProps = {
    isOpen: false,
    inTestEnvironment: false,
    createContainer: defaultCreateContainer,
    removeContainer: defaultRemoveContainer,
  };

  constructor(props) {
    super(props);

    if (
      props.isOpen &&
      typeof window !== 'undefined' &&
      !this.props.inTestEnvironment
    ) {
      this.node = this.props.createContainer();
      this.state = {
        hasNode: true,
      };
    } else {
      this.node = null;
      this.state = {
        hasNode: false,
      };
    }
  }

  /**
   * If previously closed and now open, we create a new container node and flag
   * our `hasNode` state to be true.
   * Else if previously open and now closed, we destroy our container and flag
   * our `hasNode` state to be false.
   */
  componentDidUpdate(prevProps, prevState) {
    if (!prevProps.isOpen && this.props.isOpen && !prevState.hasNode) {
      this.node = this.props.createContainer();
      this.setState({
        hasNode: true,
      });
    } else if (prevProps.isOpen && !this.props.isOpen && prevState.hasNode) {
      this.props.removeContainer(this.node);
      this.setState({
        hasNode: false,
      });
    }
  }

  componentWillUnmount() {
    if (this.node !== null) {
      this.props.removeContainer(this.node);
    }
  }

  render() {
    if (this.props.isOpen) {
      if (this.props.inTestEnvironment) {
        // This is to preserve legacy behavior (and avoid any createPortal quirks within unit tests)
        return this.props.children;
      } else if (typeof window !== 'undefined' && this.node !== null) {
        return createPortal(this.props.children, this.node);
      } else {
        return null;
      }
    } else {
      return null;
    }
  }
}

export default function Portal(props) {
  return (
    <PortalContext.Consumer>
      {({inTestEnvironment, createContainer, removeContainer}) => (
        <PortalImpl
          {...props}
          inTestEnvironment={inTestEnvironment}
          createContainer={createContainer}
          removeContainer={removeContainer}
        />
      )}
    </PortalContext.Consumer>
  );
}
